import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Flex,
  HStack,
  Spinner,
  StackDivider,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import download from 'downloadjs';
import { useHistory } from 'react-router';
import {
  Button,
  Heading,
  Input,
  Kpi,
  Pagination,
  Select,
  Table,
} from '~/components';
import { PAGE_SIZE, STORAGE } from '~/constants';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import { useDebounce, useQueryParams } from '~/hooks';
import {
  useSinisterDashboard,
  useSinistersList,
} from '~/hooks/useSinisters/useSinisters';
import { SinistersForm } from '~/pages/Portal/Sinisters/SinistersForm';
import { SinisterOrderEnum } from '~/types/sinisters';
import { SinistersMenuOptions } from '~/pages/Portal/Sinisters/SinistersMenuOptions';
import { getSinistersXlsx } from '~/services/Sinisters';
import { updateUrlParams } from '~/utils/updateUrlParams';

export const Sinisters: React.VFC = () => {
  const queryParams = useQueryParams();
  const history = useHistory();
  const location = useLocation();

  const [searchTmp, setSearchTmp] = useState(() => {
    return queryParams.get('busca') ?? '';
  });
  const [parametros, setParametros] = useState(() => {
    return {
      busca: queryParams.get('busca') ?? '',
      status: queryParams.get('status') ?? '',
      pagina: queryParams.get('pagina') ?? '1',
      paginacao: queryParams.get('paginacao') ?? PAGE_SIZE.toString(),
      orderBy: queryParams.get('orderBy') ?? '-criado_em',
    };
  });

  const { groups, headOffices, branches } = useContext(FilterContext);
  const user = JSON.parse(localStorage.getItem(`${STORAGE}:user`) || '');
  const { data, isLoading, isFetching, refetch, isError } = useSinistersList(
    Number(parametros.pagina),
    parametros.busca,
    parametros.status,
    parametros.orderBy,
    { groups, headOffices, branches },
    Number(parametros.paginacao),
  );
  const {
    data: dashboard,
    isLoading: isLoadingDashBoard,
    refetch: refetchDashboard,
  } = useSinisterDashboard({ groups, headOffices, branches });
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const toast = useToast();

  useEffect(() => {
    refetch();
  }, [parametros.pagina]);

  useDebounce(
    async () => {
      setParametros({
        ...parametros,
        busca: searchTmp,
        pagina: '1',
      });
      updateUrlParams(
        { ...parametros, busca: searchTmp, pagina: '1' },
        history,
      );
    },
    1000,
    [searchTmp],
  );

  useEffect(() => {
    setParametros({
      ...parametros,
      pagina: '1',
    });
    updateUrlParams({ ...parametros, pagina: '1' }, history);
    refetch();
    refetchDashboard();
  }, [groups, headOffices, branches]);

  useEffect(() => {
    const pagina = queryParams.get('pagina');
    setParametros({ ...parametros, pagina: pagina ?? '1' });
    updateUrlParams({ ...parametros, pagina: pagina ?? '1' }, history);
  }, [location.search]);

  const handleSinisterOrder = (order: SinisterOrderEnum) => {
    let value = '-criado_em';
    if (
      !parametros.orderBy.startsWith('-') &&
      parametros.orderBy.endsWith(order)
    ) {
      value = `-${order}`;
    } else if (!parametros.orderBy.endsWith(order)) {
      value = order;
    }
    setParametros({ ...parametros, pagina: '1', orderBy: value });
    updateUrlParams({ ...parametros, pagina: '1', orderBy: value }, history);
  };

  const handlePageSizeChange = (e: any) => {
    setParametros({ ...parametros, paginacao: e.target.value, pagina: '1' });
    updateUrlParams(
      { ...parametros, paginacao: e.target.value, pagina: '1' },
      history,
    );
  };

  function handleSinisterStatusChange(e: any) {
    setParametros({
      ...parametros,
      status: e.target.value,
      pagina: '1',
    });
    updateUrlParams(
      {
        ...parametros,
        status: e.target.value,
        pagina: '1',
      },
      history,
    );
  }

  const handleDownload = async () => {
    setIsLoadingDownload(true);
    getSinistersXlsx(parametros.busca, parametros.status, parametros.orderBy, {
      groups,
      headOffices,
      branches,
    })
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        // recuperando o erro do back
        // https://github.com/axios/axios/issues/3779
        const error2 = JSON.parse(await err.response.data.text());

        toast({
          title: `Erro`,
          description: error2.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };

  return (
    <Box px="8" py="4">
      <Heading>Sinistros</Heading>
      <Flex pt={2} flexDir="column" w="100%">
        <Flex justify="space-between" py={5}>
          <Flex align="center" gridGap={4} alignItems="flex-end">
            <Input
              name="search"
              type="text"
              icon={FiSearch}
              placeholder="Procure por N° de sinistro, Série, Equipamento, Modelo ..."
              w={500}
              value={searchTmp}
              onChange={e => setSearchTmp(e.target.value)}
              fontSize="15px"
            />
            <Select
              name="status"
              label="Status"
              onChange={e => handleSinisterStatusChange(e)}
              value={parametros.status}
            >
              <option value="">Todos</option>
              <option value="0">Novo</option>
              <option value="1">Em Análise</option>
              <option value="2">Aguardando Envio</option>
              <option value="3">Aguardando Pagamento</option>
              <option value="4">Pagamento em Análise</option>
              <option value="5">Revisando Pagamento</option>
              <option value="6">Enviado para Seguradora</option>
              <option value="7">Aguardando Entrega</option>
              <option value="8">Encerrado</option>
              <option value="9">Cancelado</option>
              <option value="10">Revisão Pendente</option>
              <option value="11">Revisado</option>
            </Select>
            {isFetching && <Spinner />}
          </Flex>
          <HStack
            gridGap={2}
            justifyContent="space-between"
            alignItems="baseline"
          >
            {user.type === 'Cliente' && <SinistersForm isEdit={false} />}
            <Button
              disabled={isLoadingDownload || isLoading}
              onClick={handleDownload}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
            >
              Exportar
              {isLoadingDownload && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
          </HStack>
        </Flex>
        <Flex marginBottom={4}>
          {user.type !== 'Cliente' && dashboard && (
            <>
              <VStack
                spacing={2}
                align="stretch"
                marginBottom="10px"
                marginRight={3}
              >
                <Kpi title=" Novo" value={dashboard.novo} defaltStyle={false} />
                <Kpi
                  title=" Em análise"
                  value={dashboard.em_analise}
                  defaltStyle={false}
                />
              </VStack>
              <VStack
                spacing={2}
                align="stretch"
                marginBottom="10px"
                marginRight={3}
              >
                <Kpi
                  title=" Revisado"
                  value={dashboard.revisado}
                  defaltStyle={false}
                />
                <Kpi
                  title=" Revisão pendente"
                  value={dashboard.revisao_pendente}
                  defaltStyle={false}
                />
              </VStack>
              <VStack
                spacing={2}
                align="stretch"
                marginBottom="10px"
                marginRight={3}
              >
                <Kpi
                  title=" Envio seguradora"
                  value={dashboard.enviando_para_seguradora}
                  defaltStyle={false}
                />
                <Kpi
                  title=" Aguardando envio"
                  value={dashboard.aguardando_envio}
                  defaltStyle={false}
                />
              </VStack>
              <VStack
                spacing={2}
                align="stretch"
                marginBottom="10px"
                marginRight={3}
              >
                <Kpi
                  title=" Aguardando pagamento"
                  value={dashboard.aguardando_pagamento}
                  defaltStyle={false}
                  variant="warning"
                />
                <Kpi
                  title=" Pagamento em análise"
                  value={dashboard.pagamento_em_analise}
                  defaltStyle={false}
                />
              </VStack>
              <VStack
                spacing={2}
                align="stretch"
                marginBottom="10px"
                marginRight={3}
              >
                <Kpi
                  title=" Revisar pagamento"
                  value={dashboard.revisando_pagamento}
                  defaltStyle={false}
                />
                <Kpi
                  title=" Aguardando entrega"
                  value={dashboard.aguardando_entrega}
                  defaltStyle={false}
                />
              </VStack>
              <VStack
                spacing={2}
                align="stretch"
                marginBottom="10px"
                marginRight={3}
              >
                <Kpi
                  title=" Encerrado"
                  value={dashboard.encerrado}
                  defaltStyle={false}
                />
                <Kpi
                  title=" Cancelado"
                  value={dashboard.cancelado}
                  defaltStyle={false}
                />
              </VStack>
            </>
          )}

          {user.type !== 'Cliente' && dashboard && (
            <VStack
              divider={<StackDivider borderColor="gray.200" />}
              spacing={2}
              align="stretch"
              marginBottom="10px"
              marginLeft="30px"
            >
              <Kpi title="Total de sinistros" value={dashboard.total} />
            </VStack>
          )}
        </Flex>

        {isLoading ? (
          <Flex justify="center">
            <Spinner />
          </Flex>
        ) : isError ? (
          <Flex justify="center">
            <Text>Falha ao obter dados dos sinistros.</Text>
          </Flex>
        ) : !isLoading && !data?.results.length ? (
          <Flex justify="center">
            <Text>Não foi encontrado nenhum sinistro.</Text>
          </Flex>
        ) : (
          <>
            <Table>
              <Thead>
                <Tr>
                  <Th>
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() =>
                        handleSinisterOrder(SinisterOrderEnum.SINISTRO)
                      }
                    >
                      N° do sinistro
                      {parametros.orderBy.startsWith('-') &&
                      parametros.orderBy.endsWith(
                        SinisterOrderEnum.SINISTRO,
                      ) ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : parametros.orderBy.endsWith(
                          SinisterOrderEnum.SINISTRO,
                        ) ? (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      ) : null}
                    </Flex>
                  </Th>
                  <Th>
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() =>
                        handleSinisterOrder(SinisterOrderEnum.SERIE)
                      }
                    >
                      Série
                      {parametros.orderBy.startsWith('-') &&
                      parametros.orderBy.endsWith(SinisterOrderEnum.SERIE) ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : parametros.orderBy.endsWith(
                          SinisterOrderEnum.SERIE,
                        ) ? (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      ) : null}
                    </Flex>
                  </Th>
                  <Th>
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() => handleSinisterOrder(SinisterOrderEnum.SKU)}
                    >
                      SKU
                      {parametros.orderBy.startsWith('-') &&
                      parametros.orderBy.endsWith(SinisterOrderEnum.SKU) ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : parametros.orderBy.endsWith(SinisterOrderEnum.SKU) ? (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      ) : null}
                    </Flex>
                  </Th>
                  <Th>
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() =>
                        handleSinisterOrder(SinisterOrderEnum.EQUIPAMENTO)
                      }
                    >
                      Equipamento / Modelo
                      {parametros.orderBy.startsWith('-') &&
                      parametros.orderBy.endsWith(
                        SinisterOrderEnum.EQUIPAMENTO,
                      ) ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : parametros.orderBy.endsWith(
                          SinisterOrderEnum.EQUIPAMENTO,
                        ) ? (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      ) : null}
                    </Flex>
                  </Th>
                  <Th>
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() =>
                        handleSinisterOrder(SinisterOrderEnum.CLIENTE)
                      }
                    >
                      Cliente / CNPJ
                      {parametros.orderBy.startsWith('-') &&
                      parametros.orderBy.endsWith(SinisterOrderEnum.CLIENTE) ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : parametros.orderBy.endsWith(
                          SinisterOrderEnum.CLIENTE,
                        ) ? (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      ) : null}
                    </Flex>
                  </Th>
                  <Th>
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() =>
                        handleSinisterOrder(SinisterOrderEnum.GRUPO)
                      }
                    >
                      Grupo
                      {parametros.orderBy.startsWith('-') &&
                      parametros.orderBy.endsWith(SinisterOrderEnum.GRUPO) ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : parametros.orderBy.endsWith(
                          SinisterOrderEnum.GRUPO,
                        ) ? (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      ) : null}
                    </Flex>
                  </Th>
                  <Th>
                    <Flex
                      gap={2}
                      cursor="pointer"
                      onClick={() =>
                        handleSinisterOrder(SinisterOrderEnum.RESPONSAVEL)
                      }
                    >
                      Responsável / Data
                      {parametros.orderBy.startsWith('-') &&
                      parametros.orderBy.endsWith(
                        SinisterOrderEnum.RESPONSAVEL,
                      ) ? (
                        <ChevronDownIcon fontSize={14} ml="2" />
                      ) : parametros.orderBy.endsWith(
                          SinisterOrderEnum.RESPONSAVEL,
                        ) ? (
                        <ChevronUpIcon fontSize={14} ml="2" />
                      ) : null}
                    </Flex>
                  </Th>
                  <Th>Tipo de sinistro</Th>
                  <Th>Status</Th>
                  <Th>Ações</Th>
                  <Td display={{ display: 'nome' }} />
                </Tr>
              </Thead>
              <Tbody>
                {data &&
                  data.results.map(sinistro => (
                    <Tr key={sinistro.id}>
                      <Td>
                        <Link to={`sinisters/${sinistro.id}`}>
                          <Text
                            _hover={{
                              color: 'gray.900',
                              textDecoration: 'underline',
                            }}
                          >
                            {sinistro.numero_sinistro}
                          </Text>
                        </Link>
                      </Td>
                      <Td>
                        <Text>{sinistro.numero_serie}</Text>
                      </Td>
                      <Td>
                        <Text>{sinistro.equipamento.sku}</Text>
                      </Td>
                      <Td>
                        <Text>{sinistro.equipamento.tipo_equipamento}</Text>
                        <Text>{sinistro.equipamento.modelo}</Text>
                      </Td>
                      <Td>
                        <Text>{sinistro.empresa.cliente}</Text>
                        <Text>{sinistro.empresa.cnpj}</Text>
                      </Td>
                      <Td>
                        <Text>{sinistro.empresa.grupo}</Text>
                      </Td>
                      <Td>
                        <Text>{sinistro.comunicado_por}</Text>
                        <Text>{sinistro.formattedDate}</Text>
                      </Td>
                      <Td>
                        <Text>{sinistro.tipo.nome}</Text>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={sinistro?.colorScheme}
                          px={2}
                          py={1}
                          borderRadius={4}
                          w="150px"
                          textAlign="center"
                          fontSize="10px"
                        >
                          {sinistro?.formattedStatus}
                        </Badge>
                      </Td>
                      <Td>
                        <SinistersMenuOptions sinistro={sinistro} />
                      </Td>
                      <Td display={{ display: 'nome' }} />
                    </Tr>
                  ))}
              </Tbody>
            </Table>
            <Flex justify="flex-end">
              <Pagination
                totalCountOfRegisters={data?.count || 0}
                currentPage={Number(parametros.pagina) ?? 1}
                onPageChange={_page => {
                  setParametros({ ...parametros, pagina: _page.toString() });
                  updateUrlParams(
                    { ...parametros, pagina: _page.toString() },
                    history,
                  );
                }}
                onPageSizeChange={handlePageSizeChange}
                registersPerPage={Number(parametros.paginacao) ?? PAGE_SIZE}
              />
            </Flex>
          </>
        )}
      </Flex>
    </Box>
  );
};
